import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ProductForm from '../../components/products/ProductForm';
import productService from '../../services/productService';
import mediaService from '../../services/mediaService';
import { addToRecentProducts } from '../../components/products/RecentProducts';
import './ProductFormPage.css';

function ProductEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await productService.getProduct(id);
        const productData = response.data;

        // Map backend sortOrder to frontend displayOrder
        if (productData?.images && Array.isArray(productData.images)) {
          productData.images = productData.images.map(img => ({
            ...img,
            displayOrder: img.sortOrder  // Map sortOrder → displayOrder
          }));
        }

        setProduct(productData);

        // Add to recent products
        addToRecentProducts(productData);
      } catch (err) {
        setError('Failed to load product');
        console.error('Error fetching product:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  /**
   * Process images - upload new ones, keep existing ones
   */
  const processImages = async (images) => {
    if (!images || images.length === 0) {
      console.log('[ProcessImages] No images to process');
      return [];
    }

    console.log('[ProcessImages] Processing', images.length, 'images');

    const processPromises = images.map(async (img, index) => {
      console.log(`[ProcessImages] Image ${index}:`, {
        hasId: !!img.id,
        isNew: img.isNew,
        hasFile: !!img.file,
        urlType: img.url?.startsWith('blob:') ? 'BLOB' : img.url?.startsWith('http') ? 'CDN' : 'UNKNOWN',
        url: img.url?.substring(0, 60) + '...'
      });

      // New image - upload it
      if (img.isNew && img.file) {
        try {
          console.log(`[ProcessImages] Uploading new image ${index} to CDN...`);
          const uploaded = await mediaService.upload(
            img.file,
            'products',
            img.altText || ''
          );
          console.log(`[ProcessImages] Upload successful:`, uploaded.url?.substring(0, 60) + '...');
          return {
            url: uploaded.url,
            isPrimary: img.isPrimary,
            displayOrder: index,
            altText: img.altText || '',
          };
        } catch (uploadError) {
          console.error('[ProcessImages] UPLOAD FAILED:', img.file.name, uploadError);
          throw new Error(`Failed to upload image: ${img.file.name}`);
        }
      }

      // Existing image - keep it
      console.log(`[ProcessImages] Keeping existing image ${index}:`, img.url?.substring(0, 60));
      return {
        id: img.id,
        url: img.url,
        isPrimary: img.isPrimary,
        displayOrder: index,
        altText: img.altText || '',
      };
    });

    const results = await Promise.all(processPromises);
    console.log('[ProcessImages] Final results:', results.map(r => ({
      hasId: !!r.id,
      urlType: r.url?.startsWith('blob:') ? '❌ BLOB' : r.url?.startsWith('http') ? '✅ CDN' : 'UNKNOWN',
      url: r.url?.substring(0, 60) + '...'
    })));

    return results;
  };

  const handleSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      // Step 1: Process images (upload new, keep existing)
      const processedImages = await processImages(data.images);

      // Step 2: Update product with processed image URLs
      const productData = {
        ...data,
        images: processedImages,
      };

      const response = await productService.updateProduct(id, productData);

      // Step 3: Map backend sortOrder to frontend displayOrder
      if (response.data?.images && Array.isArray(response.data.images)) {
        response.data.images = response.data.images.map(img => ({
          ...img,
          displayOrder: img.sortOrder  // Map sortOrder → displayOrder
        }));
      }

      // Step 4: Update product state with saved data
      setProduct(response.data);

      toast.success('Product updated successfully');
      navigate(`/products/${id}`);
    } catch (err) {
      // Display user-friendly error message
      // err.message is already sanitized by mediaService or other services
      const message = err.message || 'Failed to update product. Please try again.';
      toast.error(message);

      // Log full error for debugging (not shown to user)
      console.error('Error updating product:', err.originalError || err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="product-form-page">
        <div className="page-loading">
          <div className="loading-spinner"></div>
          <span>Loading product...</span>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-form-page">
        <div className="page-error">
          <h2>Product Not Found</h2>
          <p>{error || 'The product you are looking for does not exist.'}</p>
          <Link to="/products" className="btn">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="product-form-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Edit Product</h1>
          <p>Update {product.name}</p>
        </div>
      </div>

      <ProductForm product={product} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
    </div>
  );
}

export default ProductEditPage;
